import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextInput, Group, LoadingOverlay, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { CreateMasterAreaDto } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const items = [
  { title: 'Master Area', href: '/app/dacum/master-area' },
  { title: 'New Area', href: '' }
];

const areaSchema = z.object({
  areaName: z.string().min(1, 'Area name is required').max(255, 'Area name is too long'),
  areaCode: z
    .string()
    .min(1, 'Area code is required')
    .max(10, 'Area code must be 10 characters or less')
});

type FormData = z.infer<typeof areaSchema>;

const AddMasterAreaPage: React.FC = () => {
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      areaName: '',
      areaCode: ''
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => {
      const createDto: CreateMasterAreaDto = {
        areaName: data.areaName,
        areaCode: data.areaCode
      };

      return masterAreaService.create(createDto);
    },
    onSuccess: () => {
      navigate('/app/dacum/master-area');
      showSuccessNotification({
        title: 'Created Successfully!',
        message: 'Your area has been created successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to create area',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processSubmit = () => {
    if (formData) {
      createMutation.mutate(formData);
    }
    close();
  };

  return (
    <Container fluid>
      <PageTitle title='Add Area' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Submit'
        statusProcess='submit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processSubmit}
        itemLabel='area'
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={createMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='areaCode'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Area Code'
                  placeholder='Enter area code (e.g., JKT-SLT)'
                  error={errors.areaCode?.message}
                  required
                  description='Short code to identify the area (max 10 characters)'
                  {...field}
                />
              )}
            />

            <Controller
              name='areaName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Area Name'
                  placeholder='Enter area name (e.g., Area Jakarta Selatan)'
                  error={errors.areaName?.message}
                  required
                  description='Full name of the area'
                  {...field}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton
              text='Cancel'
              onClick={() => navigate('/app/dacum/master-area')}
              mr={10}
            />
            <PrimaryButton
              text='Save'
              type='submit'
              loading={isSubmitting || createMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </Container>
  );
};

export default AddMasterAreaPage;
